import { useCallback, useSyncExternalStore } from 'react'

export type PlaybackState = {
  readonly id: string | null
  readonly playing: boolean
  readonly positionMs: number
  readonly speed: number
}

const SPEEDS = [1, 1.5, 2] as const

let audio: HTMLAudioElement | null = null
let state: PlaybackState = { id: null, playing: false, positionMs: 0, speed: 1 }
const listeners = new Set<() => void>()
const endListeners = new Set<(id: string) => void>()
const startListeners = new Set<(id: string) => void>()

function emit(next: Partial<PlaybackState>): void {
  state = { ...state, ...next }
  for (const l of listeners) l()
}

function element(): HTMLAudioElement {
  if (audio) return audio
  const el = new Audio()
  el.preload = 'metadata'
  el.addEventListener('timeupdate', () => emit({ positionMs: el.currentTime * 1000 }))
  el.addEventListener('play', () => emit({ playing: true }))
  el.addEventListener('pause', () => emit({ playing: false }))
  el.addEventListener('ended', () => {
    const id = state.id
    emit({ playing: false, positionMs: 0 })
    if (id) for (const l of endListeners) l(id)
  })
  audio = el
  return el
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function onVoiceEnded(listener: (id: string) => void): () => void {
  endListeners.add(listener)
  return () => endListeners.delete(listener)
}

export function onVoiceStarted(listener: (id: string) => void): () => void {
  startListeners.add(listener)
  return () => startListeners.delete(listener)
}

export function stopVoice(): void {
  if (!audio) return
  audio.pause()
  emit({ id: null, playing: false, positionMs: 0 })
}

export function toggleVoice(id: string, src: string, atFraction?: number): void {
  const el = element()
  if (state.id !== id) {
    el.pause()
    el.src = src
    el.playbackRate = state.speed
    emit({ id, positionMs: 0, playing: false })
    el.addEventListener(
      'loadedmetadata',
      () => {
        if (atFraction !== undefined && Number.isFinite(el.duration)) el.currentTime = el.duration * atFraction
      },
      { once: true },
    )
    for (const l of startListeners) l(id)
    void el.play().catch(() => emit({ playing: false }))
    return
  }
  if (atFraction !== undefined) {
    if (Number.isFinite(el.duration)) el.currentTime = el.duration * atFraction
    if (el.paused) void el.play().catch(() => emit({ playing: false }))
    return
  }
  if (el.paused) void el.play().catch(() => emit({ playing: false }))
  else el.pause()
}

export function cycleVoiceSpeed(): void {
  const i = SPEEDS.indexOf(state.speed as (typeof SPEEDS)[number])
  const speed = SPEEDS[(i + 1) % SPEEDS.length] ?? 1
  if (audio) audio.playbackRate = speed
  emit({ speed })
}

export function useVoicePlayback(id: string): PlaybackState & { active: boolean } {
  const snapshot = useSyncExternalStore(subscribe, () => state, () => state)
  return { ...snapshot, active: snapshot.id === id }
}

export function useVoiceToggle(id: string, src: string | null) {
  return useCallback(
    (atFraction?: number) => {
      if (src) toggleVoice(id, src, atFraction)
    },
    [id, src],
  )
}
